<template>
  <!-- position: relative so the loading veil at the bottom covers this card
       and nothing else, as Vuetify 2's `absolute` overlay did. -->
  <div style="position: relative" :class="{ 'tk-plate': plate }">
    <v-form ref="observer" @submit.prevent>
      <v-card>
        <!-- The hooldusplaat look: what the order is and when, above a rule. -->
        <div v-if="plate" class="tk-head" :class="{ 'tk-head--deleted': isDeleted }">
          <div class="tk-head__text">
            <h2 class="tk-title tk-wide">{{ id ? 'Töökäsk' : 'Uus töökäsk' }}<span v-if="isDeleted"> (kustutatud)</span></h2>
            <span class="tk-cap tk-mid">{{ headCaption }}</span>
          </div>
          <v-btn
            v-if="showAkt"
            variant="outlined"
            size="small"
            color="#2E7D32"
            :href="aktUrl"
            target="_blank"
            rel="noopener"
          >
            <v-icon start size="small">mdi-file-document-edit-outline</v-icon>
            Akt
          </v-btn>
          <v-menu location="bottom end" v-if="id && !isDeleted">
            <template #activator="{ props }">
              <v-btn icon variant="text" aria-label="Rohkem" v-bind="props">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item v-if="canEdit('event', eventModel)" @click="edit">
                <v-list-item-title>Muuda</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDelete('event', eventModel)" @click="confirmDelete = true">
                <v-list-item-title>Kustuta</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn icon variant="text" aria-label="Sulge" @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-toolbar v-else :color="isDeleted ? 'red' : 'primary'" theme="dark">
          <v-toolbar-title>Töökäsk <strong v-if="isDeleted">(kustutatud)</strong></v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            v-if="showAkt"
            variant="text"
            size="small"
            class="mr-2"
            :href="aktUrl"
            target="_blank"
            rel="noopener"
          >
            <v-icon start size="small">mdi-file-document-edit-outline</v-icon>
            Akt
          </v-btn>
          <v-btn icon @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-menu location="bottom end" v-if="id && !isDeleted">
            <template #activator="{ props }">
              <v-btn icon v-bind="props">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item v-if="id && !isDeleted && canEdit('event', eventModel)" @click="edit">
                <v-list-item-title>Muuda</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="id && !isDeleted && canDelete('event', eventModel)" @click="confirmDelete = true">
                <v-list-item-title>Kustuta</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar>
        <v-card-text class="pb-4">
          <v-container>
            <v-row>
              <v-col cols="12" sm="8" class="pt-0 pb-6 pb-sm-0" :class="{ 'tk-main': plate }">
                <v-row>
                  <v-col cols="12" sm="6">
                    <div v-if="plate" class="tk-lbl">Algus</div>
                    <date-time-field
                      v-model="form.startDate"
                      :label="plate ? undefined : 'Algus'"
                      :rules="[rules.required]"
                      v-bind="commonFieldProps"
                    >
                      <template v-if="plate && mark.start" #append-inner>
                        <span class="tk-chip tk-chip--auto">kalendrist</span>
                      </template>
                    </date-time-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div v-if="plate" class="tk-lbl">Lõpp</div>
                    <date-time-field
                      v-model="form.endDate"
                      :label="plate ? undefined : 'Lõpp'"
                      :rules="[rules.required]"
                      v-bind="commonFieldProps"
                    />
                  </v-col>
                  <v-col cols="12">
                    <div v-if="plate" class="tk-lbl">Aadress</div>
                    <autocomplete
                      v-model="form.object"
                      :label="plate ? undefined : 'Aadress'"
                      :rules="[rules.required]"
                      item-text="fullAddress"
                      item-value="id"
                      searchMethod="searchObjects"
                      fallbackSearchMethod="searchAdsAddresses"
                      itemKey="objects"
                      hide-no-data
                      v-bind="commonFieldProps"
                    />
                  </v-col>
                  <v-col cols="12">
                    <div v-if="plate" class="tk-lbl">Klient</div>
                    <autocomplete
                      v-model="form.client"
                      :label="plate ? undefined : 'Klient'"
                      :rules="[rules.required]"
                      item-text="fullName"
                      item-value="id"
                      searchMethod="searchClients"
                      itemKey="clients"
                      hide-no-data
                      v-bind="commonFieldProps"
                    >
                      <template v-if="plate && mark.client" #append-inner>
                        <span class="tk-chip tk-chip--auto">objekti järgi</span>
                      </template>
                    </autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div v-if="plate" class="tk-lbl">E-posti aadress</div>
                    <v-text-field
                      v-model="form.email"
                      type="email"
                      inputmode="email"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck="false"
                      :label="plate ? undefined : 'E-posti aadress'"
                      v-bind="commonFieldProps"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div v-if="plate" class="tk-lbl">Telefon</div>
                    <v-text-field
                      v-model="form.phone"
                      type="tel"
                      :label="plate ? undefined : 'Telefon'"
                      v-bind="commonFieldProps"
                    >
                      <!-- On a phone the number is for ringing, not for copying
                           out of a text box. -->
                      <template v-if="form.phone" #append-inner>
                        <a :href="'tel:' + String(form.phone).replace(/[^\d+]/g, '')" class="tk-call" aria-label="Helista" title="Helista" @click.stop><v-icon size="20">mdi-phone</v-icon></a>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <div v-if="plate" class="tk-lbl">Tehnik</div>
                    <v-select
                      v-model="form.technic"
                      :items="technics"
                      item-title="fullName"
                      item-value="id"
                      :label="plate ? undefined : 'Tehnik'"
                      :rules="[rules.required]"
                      v-bind="commonFieldProps"
                    >
                      <!-- What the planner says each technician is doing that day
                           (Kontoris, Valves, Puhkusel, …), on the name's own line. -->
                      <template v-if="plate && technicDay.length" #append-inner>
                        <span v-for="d in technicDay" :key="d.name" class="tk-day">
                          <i class="tk-day__dot" :style="{ background: d.color }" />{{ d.name }}
                        </span>
                      </template>
                      <template v-if="plate" #item="{ props, item }">
                        <v-list-item v-bind="props" :title="undefined">
                          <v-list-item-title class="tk-tech">
                            <span class="tk-tech__name">{{ item.title }}</span>
                            <span v-for="d in plannerFor(item.raw.id)" :key="d.name" class="tk-day">
                              <i class="tk-day__dot" :style="{ background: d.color }" />{{ d.name }}
                            </span>
                          </v-list-item-title>
                        </v-list-item>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12">
                    <!-- Seade: everything installed at this address, with the
                         serial numbers, so the technician knows what he is
                         going to find. It used to be a text field, and typing
                         into it created a device row — which is how an address
                         ended up with four boilers and no idea which one is
                         there. Equipment is kept on the object's Seadmed page;
                         this shows it, and the button in the box opens that
                         list without leaving a half-typed work order — offered
                         while the order is being read as much as written, since
                         the equipment belongs to the address, not the visit.

                         append-inner: Vuetify 2's `append` slot sat inside the
                         box; Vuetify 3's sits outside it. -->
                    <div v-if="plate" class="tk-lbl">Seade</div>
                    <v-textarea
                      :model-value="deviceSummary"
                      :label="plate ? undefined : 'Seade'"
                      :rows="1"
                      auto-grow
                      v-bind="deviceFieldProps"
                    >
                      <template v-if="!devicesBelow" #append-inner>
                        <v-btn
                          v-if="form.object && form.object.id"
                          size="x-small"
                          variant="text"
                          color="primary"
                          class="mt-n1"
                          @click="devicesDialog = true"
                        >
                          Halda seadmeid
                        </v-btn>
                      </template>
                    </v-textarea>
                    <!-- On a phone the button inside the box squeezed the list
                         into a sliver; there it goes under the list instead. -->
                    <v-btn
                      v-if="devicesBelow && form.object && form.object.id"
                      size="x-small"
                      variant="text"
                      color="primary"
                      class="tk-below px-1"
                      @click="devicesDialog = true"
                    >
                      Halda seadmeid
                    </v-btn>
                  </v-col>
                  <v-col cols="12">
                    <div v-if="plate" class="tk-lbl">Objekti kirjeldus</div>
                    <v-textarea
                      v-model="form.objectNotes"
                      :label="plate ? undefined : 'Objekti kirjeldus'"
                      :rows="1"
                      auto-grow
                      v-bind="commonFieldProps"
                    />
                  </v-col>
                  <v-col cols="12">
                    <!-- The new look calls it what it is for: the work to be done. -->
                    <div v-if="plate" class="tk-lbl">Töökäsk</div>
                    <v-textarea
                      v-model="form.comments"
                      :label="plate ? undefined : 'Kommentaarid'"
                      :rows="1"
                      auto-grow
                      v-bind="commonFieldProps"
                      :placeholder="plate ? 'Mida tehnik peab tegema' : commonFieldProps.placeholder"
                    />
                  </v-col>
                  <v-col cols="6" v-if="isNotTechnic">
                    <div v-if="plate" class="tk-lbl">Makseviis</div>
                    <v-select
                      v-model="form.paymentType"
                      :items="paymentTypes"
                      item-title="text"
                      item-value="value"
                      :label="plate ? undefined : 'Makseviis'"
                      v-bind="commonFieldProps"
                    />
                  </v-col>
                  <v-col cols="6" v-if="isNotTechnic">
                    <div v-if="plate" class="tk-lbl">Summa</div>
                    <v-text-field
                      v-model="form.payment"
                      inputmode="decimal"
                      :label="plate ? undefined : 'Summa'"
                      v-bind="commonFieldProps"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" sm="4" class="pt-0 pb-0" :class="{ 'tk-side': plate }">
                <v-row>
                  <v-col cols="12">
                    <div v-if="plate" class="tk-lbl">Töökäsu tüüp</div>
                    <v-select
                      v-model="form.category"
                      @update:model-value="categoryPayment"
                      :items="calendarCategoryOptions"
                      item-title="text"
                      item-value="value"
                      :label="plate ? undefined : 'Töökäsu tüüp'"
                      :rules="[rules.required]"
                      v-bind="commonFieldProps"
                    >
                      <template v-if="plate && mark.category" #append-inner>
                        <span class="tk-chip tk-chip--auto">objekti järgi</span>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" v-if="isNotTechnic">
                    <div v-if="plate" class="tk-lbl">Tuleviku hoolduse meeldetuletus</div>
                    <v-select
                      v-model="form.reminders"
                      :items="['SMS', 'EMAIL']"
                      :label="plate ? undefined : 'Tuleviku hoolduse meeldetuletus'"
                      multiple
                      v-bind="commonFieldProps"
                    />
                  </v-col>
                  <v-col cols="12" class="event-toggles py-1">
                    <v-switch
                      v-model="form.visitReminderSms"
                      class="ma-0 pa-0"
                      label="Külastuse meeldetuletus"
                      color="green"
                      v-bind="switchProps"
                    />
                    <v-switch
                      v-model="form.problematicClient"
                      class="ma-0 pa-0"
                      label="Probleemne klient"
                      color="error"
                      v-bind="switchProps"
                      :disabled="!isNotTechnic"
                    />
                    <v-switch
                      v-model="form.problematicObject"
                      class="ma-0 pa-0"
                      label="Probleemne objekt"
                      color="error"
                      v-bind="switchProps"
                      :disabled="!isNotTechnic"
                    />
                    <v-btn
                      v-if="form.object && form.object.id"
                      class="px-1 mt-1"
                      size="small"
                      variant="text"
                      color="primary"
                      @click="openNotes()"
                    >
                      <v-icon size="small" start>mdi-note-text-outline</v-icon>
                      Objekti märkused{{notesCount != null ? ' (' + notesCount + ')' : ''}}
                    </v-btn>
                  </v-col>
                  <!-- Why the client / the object is Probleemne, read-only: it is
                       written on the client's and the object's own form. -->
                  <v-col v-if="problemReasons.length" cols="12" class="tk-reasons py-1">
                    <div v-for="reason in problemReasons" :key="reason.key" class="tk-reason">
                      <span class="tk-reason__cap">{{ reason.cap }}</span>
                      <span>{{ reason.text }}</span>
                    </div>
                  </v-col>
                  <!-- The hooldusplaat look: the address's work orders, one line each,
                       time then date; blue still to come, green done. -->
                  <v-col v-if="plate && mode !== 'view' && history.rows.length" cols="12" class="tk-hist">
                    <div class="tk-hist__title tk-mid">TÖÖKÄSUD SIIN</div>
                    <button
                      v-for="row in history.rows"
                      :key="row.id"
                      type="button"
                      class="tk-hist__row"
                      @click="viewEvent(row.event)"
                    >
                      <i class="tk-dot" :class="row.future ? 'tk-dot--future' : 'tk-dot--past'" />
                      <span class="tk-hist__line"><b>{{ row.time }}</b>{{ row.date }}</span>
                    </button>
                  </v-col>
                  <v-col v-if="!plate && mode !== 'view' && latestEvents.length" cols="12">
                    <strong>Varasemad töökäsud</strong>
                    <v-list density="compact">
                      <v-list-item v-for="(item, i) in latestEvents" :key="i" class="pa-0" @click="viewEvent(item)">
                        <v-list-item-title>
                          <v-chip class="mr-1" size="small" variant="flat" :color="eventColor(item)" />
                          {{eventPeriod(item)}}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <template v-if="plate && formMode !== 'view' && (canEdit('event', eventModel) || !update)">
          <div class="tk-foot">
            <span class="tk-foot__hint">{{ footHint }}</span>
            <v-btn variant="outlined" color="#2E7D32" class="tk-btn" @click="$emit('close')">Katkesta</v-btn>
            <v-btn variant="flat" color="#2E7D32" class="tk-btn" :loading="submitLoading" @click="_submit()">Salvesta</v-btn>
          </div>
        </template>
        <template v-else-if="formMode !== 'view' && (canEdit('event', eventModel) || !update)">
          <v-divider />
          <v-card-actions class="pa-4">
            <v-btn :loading="submitLoading" @click="_submit()" color="primary">Salvesta</v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-form>
    <v-dialog v-model="selectedEvent" max-width="700px">
      <event mode="view" :eventId="selectedEventId" @close="selectedEvent = false" />
    </v-dialog>
    <!-- The object's own Seadmed list, opened without leaving the half-typed
         work order. It loads and saves for itself, and tells us when the list
         changed so the line above can be rewritten. -->
    <v-dialog v-model="devicesDialog" max-width="900px" scrollable>
      <device-list
        v-if="devicesDialog && form.object && form.object.id"
        :objectId="form.object.id"
        editable
        deletable
        show-archived
        @changed="devices = $event"
      />
    </v-dialog>
    <!-- The object's Märkused tab, opened over the work order the way Seadmed
         is: read what earlier visits left, add one, and come back to the
         Töökäsk as it was. -->
    <!-- Full screen on a phone: the list is the whole point of opening it, and
         a dialog's side margins leave little room for a note and its photos. -->
    <v-dialog v-model="notesDialog" max-width="900px" scrollable :fullscreen="$vuetify.display.xs">
      <v-card v-if="notesDialog">
        <v-card-title class="text-subtitle-1 py-2 d-flex align-center">
          <div v-if="$vuetify.display.xs" class="notes-title">
            <div>Märkused</div>
            <div class="text-caption text-medium-emphasis text-truncate">{{notesObject ? $address(notesObject.fullAddress) : ''}}</div>
          </div>
          <template v-else>Märkused — {{notesObject ? $address(notesObject.fullAddress) : ''}}</template>
          <v-spacer />
          <v-btn icon variant="text" size="small" @click="notesDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text :class="$vuetify.display.xs ? 'px-3 pt-2' : 'pt-3'">
          <v-progress-linear v-if="!notesObject" indeterminate color="primary" />
          <object-notes
            v-else
            embedded
            :objectId="notesObject.id"
            :object="notesObject"
            @changed="notesCount = $event"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <confirm
      title="Vali tühistamise põhjus"
      confirmTitle="Kinnita"
      cancelTitle="Katkesta"
      @cancel="confirmDelete = null"
      @confirm="destroy()"
      :visible="!!confirmDelete"
    >
      <v-form ref="destroy" @submit.prevent>
        <v-select
          v-model="deleteReason"
          :items="calendarDeleteReasonOptions"
          item-title="text"
          item-value="value"
          label="Põhjus"
          :rules="[rules.required]"
        />
      </v-form>
    </confirm>
    <v-overlay
      :model-value="loading"
      contained
      persistent
      class="align-center justify-center"
      transition="fade-transition"
    >
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import api from './../api'
import { mapState } from 'vuex'
import formMixin from '@/plugins/formMixin'
import helpersMixin from '@/plugins/helpersMixin'
import DeviceList from '@/components/DeviceList.vue'
import ObjectNotes from '@/views/object/_notes.vue'
import { deviceSummary } from '@/plugins/devices'
import rules from '@/plugins/rules'
import loadArchivo from '@/plugins/archivo'

const dateFormat = 'YYYY-MM-DDTHH:mm'
// Planeerija's Valves and Kontorivalve.
const VALVE_WORKDAYS = [3, 8]

const paymentTypes = {
  UNPAID: 0,
  BILL: 1,
  CASH: 2,
  FREE: 3
}

const empty = {
  startDate: null,
  endDate: null,
  object: undefined,
  client: undefined,
  phone: undefined,
  email: undefined,
  objectNotes: null,
  comments: null,
  problematicClient: false,
  problematicObject: false,
  technic: null,
  paymentType: 0,
  payment: '',
  category: 0,
  reminders: [],
  visitReminderSms: false
}

export default {
  mixins: [
    formMixin,
    helpersMixin
  ],
  components: { DeviceList, ObjectNotes },
  emits: ['close', 'created', 'updated', 'deleted'],
  props: {
    event: {
      required: false,
      type: Object
    },
    eventId: {
      required: false
    },
    mode: {
      required: false,
      default: 'normal'
    }
  },
  data () {
    return {
      manualMode: undefined,
      rules,
      loading: false,
      eventModel: undefined,
      schedule: {
        onCall: [],
        inOffice: []
      },
      confirmDelete: false,
      deleteReason: undefined,
      allTechnics: [],
      update: false,
      id: null,
      latestEvents: [],
      // Every work order at the address, upcoming ones included, for the
      // hooldusplaat look's list; latestEvents above is the original look's.
      addressEvents: [],
      // What the dialog filled in by itself, as it filled it in. A mark shows
      // while the field still holds that value and goes once someone changes it.
      auto: { start: null, client: null, category: null, technic: null },
      // Makseviis was set Tasuta by choosing Garantii, not by a person — so
      // moving off Garantii may put Maksmata back. See categoryPayment().
      autoFree: false,
      // What is installed at the address, as the Seade line reads it. Fetched
      // rather than taken from the event payload, because the office can change
      // it in the dialog below without the work order being reloaded.
      devices: [],
      devicesDialog: false,
      notesDialog: false,
      notesObject: null,
      notesCount: null,
      form: {
        ...empty
      },
      paymentTypes: [{
        value: paymentTypes.UNPAID,
        text: 'Maksmata'
      }, {
        value: paymentTypes.BILL,
        text: 'Arvega'
      }, {
        value: paymentTypes.CASH,
        text: 'Sularaha'
      }, {
        value: paymentTypes.FREE,
        text: 'Tasuta'
      }]
    }
  },
  watch: {
    event: {
      handler (event) {
        this.init(event)
      },
      deep: true
    },
    eventId (id) {
      this.init(id)
    },
    'form.object' (obj) {
      this.getLatestVisits()

      if (!obj) {
        return
      }
      if (obj.client) {
        this.form.client = obj.client
        this.auto.client = obj.client
      }
      if (obj.notes) {
        this.form.objectNotes = obj.notes
      }
      this.loadDevice(obj.id)
      this.loadNotesCount(obj.id)
      if (!this.id) {
        const valve = this.valveDay = this.isOwnValveDay()
        this.setDefaultCategory(valve ? this.calendarCategories.CALL : this.objectCategory(obj), !valve)
      }
      this.form.problematicObject = obj.problematic
      this.form.reminders = []
      if (obj.reminderEmail) {
        this.form.reminders.push('EMAIL')
      }
      if (obj.reminderSms) {
        this.form.reminders.push('SMS')
      }
    },
    // E-mail, phone and Probleemne belong to the chosen client, and saving
    // writes them onto that client's card. Leaving a client (X, another
    // client, a typed-in new name) clears them, so one client's contacts never
    // end up on the next one; a picked client fills them as it is, gaps too.
    // A name being typed letter by letter is not a client yet, so a phone
    // typed beside it stays.
    'form.client' (client, old) {
      if (old && old.id && !(client && client.id === old.id)) {
        this.form.email = ''
        this.form.phone = ''
        this.form.problematicClient = false
      }
      if (!client || !client.id) {
        return
      }
      this.form.email = client.email || ''
      this.form.phone = client.phone || ''
      this.form.problematicClient = !!client.problematic
    },
    'form.technic' () {
      this.updateValveDay()
    },
    'form.startDate' (date) {
      if (!date) {
        return
      }

      this.schedule = {}

      // Numbered, and with a catch: the day's planner is a help on the form,
      // not a reason for an unhandled error, and an answer for a date already
      // changed must not be shown as today's.
      const request = this.plannerRequest = (this.plannerRequest || 0) + 1
      api.getPlanner({
        startDate: dayjs(date, dateFormat).toISOString(),
        endDate: dayjs(date, dateFormat).toISOString()
      }).then(({ events }) => {
        if (request !== this.plannerRequest) return
        this.schedule.onCall = events.filter(event => event.workday.id === 3)
        this.schedule.inOffice = events.filter(event => event.workday.id === 2)
        // Every planner row of the day, for the hooldusplaat look's Tehnik line.
        this.schedule.all = events
        this.updateTechnic()
        this.updateValveDay()
      }).catch(() => {})
    },
    plate: {
      handler (on) {
        if (!on) return
        loadArchivo()
      },
      immediate: true
    }
  },
  computed: {
    problemReasons () {
      const note = record => record && typeof record === 'object' && record.problematicNote
      return [
        this.form.problematicClient && note(this.form.client) && { key: 'client', cap: 'KLIENT · PÕHJUS', text: this.form.client.problematicNote },
        this.form.problematicObject && note(this.form.object) && { key: 'object', cap: 'OBJEKT · PÕHJUS', text: this.form.object.problematicNote }
      ].filter(Boolean)
    },
    ...mapState(['user', 'features']),
    // The plate look on a phone: Halda seadmeid under the Seade list rather
    // than inside its box.
    devicesBelow () {
      return this.plate && this.$vuetify.display.xs
    },
    // Süsteem -> Seaded -> Töökäsk uues kujunduses.
    plate () {
      return !!(this.features && this.features.eventPlate)
    },
    // Which fields still hold what the dialog put there by itself.
    mark () {
      return {
        start: !!this.auto.start && this.form.startDate === this.auto.start,
        client: !!this.auto.client && this.form.client === this.auto.client,
        category: this.auto.category !== null && !this.id && this.form.category === this.auto.category,
        technic: !!this.auto.technic && this.form.technic === this.auto.technic
      }
    },
    // A technician whose account is switched off is not offered. The one an
    // old order already has stays, so the order still shows his name.
    technics () {
      return this.allTechnics.filter(user => user.active !== false || user.id === this.form.technic)
    },
    technicDay () {
      return this.plannerFor(this.form.technic)
    },
    // "E 8. JUUNI · 13:30–15:00" over the title.
    headCaption () {
      const start = this.form.startDate && dayjs(this.form.startDate, dateFormat)
      const end = this.form.endDate && dayjs(this.form.endDate, dateFormat)
      if (!start || !start.isValid()) return ''
      const days = ['P', 'E', 'T', 'K', 'N', 'R', 'L']
      const months = ['jaan', 'veebr', 'märts', 'apr', 'mai', 'juuni', 'juuli', 'aug', 'sept', 'okt', 'nov', 'dets']
      return days[start.day()] + ' ' + start.date() + '. ' + months[start.month()] +
        (start.year() !== dayjs().year() ? ' ' + start.year() : '') +
        ' · ' + start.format('HH:mm') + (end && end.isValid() ? '–' + end.format('HH:mm') : '')
    },
    footHint () {
      const from = []
      if (this.mark.start) from.push('aeg kalendrist')
      if (this.mark.technic) from.push('tehnik planeerijast')
      if (this.mark.client || this.mark.category) from.push('klient ja tüüp objekti andmetest')
      return from.length ? 'Täidetud: ' + from.join(', ') : ''
    },
    // The address's work orders: the next few coming up (nearest first), then
    // the last ones done (newest first). Time then date, nothing else.
    history () {
      const now = dayjs()
      const row = event => {
        const start = dayjs(event.startDate)
        const end = dayjs(event.endDate)
        return {
          id: event.id,
          event,
          future: start.isAfter(now),
          time: start.format('HH:mm') + '–' + end.format('HH:mm'),
          date: start.format('DD.MM.YYYY')
        }
      }
      const rows = this.addressEvents.map(row)
      const future = rows.filter(r => r.future).sort((a, b) => dayjs(a.event.startDate) - dayjs(b.event.startDate)).slice(0, 3)
      const past = rows.filter(r => !r.future).slice(0, 5)
      return { rows: future.concat(past) }
    },
    deviceSummary () {
      return deviceSummary(this.devices)
    },
    // Vuetify 2 floated a field's label whenever it had a placeholder, which is
    // what the blank one was for: every label on the work order sits above its
    // box, filled in or not. Vuetify 3 says that as persistentPlaceholder.
    commonFieldProps () {
      return {
        hideDetails: 'auto',
        variant: 'outlined',
        density: 'compact',
        placeholder: ' ',
        persistentPlaceholder: true,
        readonly: this.formMode === 'view'
      }
    },
    // The same for a switch, which has neither a box nor a placeholder.
    switchProps () {
      return {
        hideDetails: 'auto',
        density: 'compact',
        readonly: this.formMode === 'view'
      }
    },
    // Seade is one of the fields to look at, not one to type in — in any mode.
    // The placeholder is persistent so an address with nothing on it says why
    // in the box, where the grey line under the old hand-drawn label used to.
    deviceFieldProps () {
      return {
        ...this.commonFieldProps,
        readonly: true,
        persistentPlaceholder: true,
        placeholder: this.form.object && this.form.object.id
          ? 'Seadmeid pole lisatud'
          : 'Seadmed saab lisada pärast töökäsu salvestamist'
      }
    },
    isNotTechnic () {
      return this.$hasRole(['admin', 'secretary', 'root'])
    },
    showClient () {
      return this.isNotTechnic
    },
    isDeleted () {
      return this.eventModel && this.eventModel.status === 'deleted'
    },
    // The akt app is a separate deployment-optional module, so the button only
    // appears where it can actually lead somewhere: the module is mounted, the
    // user may fill acts, and the visit is saved and still has its object.
    showAkt () {
      return !!this.features.akt &&
        !!this.id &&
        !this.isDeleted &&
        !!(this.form.object && this.form.object.id) &&
        this.$hasRole(['tehnic', 'admin', 'root'])
    },
    // Opens the act this visit already has, or starts one — akt resolves that
    // from the calendarId, so clicking twice never yields two acts.
    aktUrl () {
      const params = {
        objectId: this.form.object && this.form.object.id,
        calendarId: this.id,
        clientId: this.form.client && this.form.client.id
      }
      const query = Object.keys(params)
        .filter(key => params[key])
        .map(key => key + '=' + encodeURIComponent(params[key]))
        .join('&')
      return '/akt/new?' + query
    },
    formMode () {
      return this.manualMode || this.mode
    }
  },
  methods: {
    // Garantii work is not paid for: choosing it turns Maksmata into Tasuta.
    // Only Maksmata — Arvega / Sularaha are somebody's answer — and only on a
    // change made here, never while an existing töökäsk loads. Off Garantii
    // again, a Tasuta this put there goes back to Maksmata; one a person
    // chose stays. The server does the same for technicians, who have no
    // Makseviis field (controllers/calendar.js).
    categoryPayment (category) {
      if (category === this.calendarCategories.GUARANTEE) {
        if (this.form.paymentType === paymentTypes.UNPAID) {
          this.form.paymentType = paymentTypes.FREE
          this.autoFree = true
        }
      } else if (this.autoFree && this.form.paymentType === paymentTypes.FREE) {
        this.form.paymentType = paymentTypes.UNPAID
        this.autoFree = false
      }
    },
    async init (eventInput) {
      if (!eventInput) {
        return
      }

      this._reset()
      let data
      this.eventModel = undefined
      this.manualMode = undefined
      this.deleteReason = undefined
      this.form = { ...empty }
      this.auto = { start: null, client: null, category: null, technic: null }
      this.autoFree = false
      this.valveDay = false
      this.categoryDefault = null

      if (Number.isInteger(eventInput)) {
        this.loading = true
        this.eventModel = undefined
        const { event } = await api.getEvent(eventInput)
        this.eventModel = event
        this.loading = false
        data = event 
        this.form.startDate = dayjs(event.startDate).format(dateFormat)
        this.form.endDate = dayjs(event.endDate).format(dateFormat)
      } else {
        this.form.startDate = dayjs(eventInput.start).format(dateFormat)
        this.form.endDate = dayjs(eventInput.end).format(dateFormat)
        if (dayjs(eventInput.end).diff(dayjs(eventInput.start), 'minutes') <= 15) {
           this.form.endDate = dayjs(eventInput.start).add(90, 'minutes').format(dateFormat)
        }
        this.auto.start = this.form.startDate
        // A technician adds work for themselves, also one who has an
        // office role on top; the office gets the planner's Kontoris person.
        if (this.$hasRole(['tehnic'])) {
          this.form.technic = this.user.userId
        }
        if (this.isNotTechnic) {
          this.updateTechnic()
        } else {
          this.form.technic = this.user.userId
          this.form.category = this.calendarCategories.CALL
        }
      }

      this.update = !!data
      this.id = data && data.id

      if (!data) {
        this.categoryDefault = this.form.category
        this.updateValveDay()
        return
      }

      this.form.object = data.object || ''
      this.form.client = data.client || ''
      this.form.phone = data.client && data.client.phone
      this.form.email = data.client && data.client.email
      this.form.objectNotes = data.object && data.object.notes
      this.loadDevice(data.object && data.object.id)
      this.loadNotesCount(data.object && data.object.id)
      this.form.comments = data.comments
      this.form.problematicObject = data.object && data.object.problematic
      this.form.problematicClient = data.client && data.client.problematic
      this.form.technic = data.user && data.user.id
      this.form.payment = data.payment.amount
      this.form.paymentType = data.payment.type
      this.form.category = data.category
      this.form.visitReminderSms = data.reminderSms
    },
    async handleForm () {
      const request = {
        startDate: dayjs(this.form.startDate, dateFormat).toISOString(),
        endDate: dayjs(this.form.endDate, dateFormat).toISOString(),
        category: this.form.category,
        objectId: this.form.object && this.form.object.id,
        address: !this.form.object || !this.form.object.id
          ? (this.form.object && (this.form.object.submitAddress || this.form.object.fullAddress || this.form.object))
          : undefined,
        clientId: this.form.client && this.form.client.id,
        clientName: !this.form.client || !this.form.client.id ? this.form.client : undefined,
        technicId: this.form.technic,
        comments: this.form.comments,
        objectNotes: this.form.objectNotes,
        // An empty phone fails the server's isMobilePhone, and the phone is
        // never cleared on the card anyway (portal password), so say nothing.
        phone: this.form.phone || undefined,
        email: this.form.email,
        problematicClient: this.form.problematicClient,
        problematicObject: this.form.problematicObject,
        reminderEmail: this.form.reminders.includes('EMAIL'),
        reminderSms: this.form.reminders.includes('SMS'),
        visitReminderSms: this.form.visitReminderSms,
        paymentType: this.form.paymentType,
        payment: this.form.payment
      }
  
      if (this.update) {
        await api.updateEvent(this.id, request)
        this.showNotification({ text: 'Edukalt uuendatud', color: 'green' })
        this.$emit('updated')
      } else {
        await api.createEvent(request)
        this.$emit('created')
      }
    },
    // The object is loaded whole rather than taken from the work order: the
    // note form writes the object's Aktiivne back with every note, and the
    // copy a work order carries (or an address just picked in it) is not
    // guaranteed to say what that is.
    async openNotes () {
      const objectId = this.form.object && this.form.object.id
      this.notesObject = null
      this.notesDialog = true
      try {
        const { object } = await api.getObject(objectId)
        if (this.notesDialog && this.form.object && this.form.object.id === objectId) {
          this.notesObject = object
        }
      } catch (err) {
        this.notesDialog = false
        this.showNotification({ text: err.message, color: 'red' })
      }
    },
    // How many märkused the object has, for the link under the switches. The
    // answer to a lookup for an address the form has since moved off is dropped.
    async loadNotesCount (objectId) {
      this.notesCount = null
      if (!objectId) {
        return
      }
      try {
        const { count } = await api.getObjectNotes(objectId, { limit: 1 })
        if (this.form.object && this.form.object.id === objectId) {
          this.notesCount = count
        }
      } catch (err) {
        // The link still opens the list, which reports its own error.
      }
    },
    // Everything installed at the address, which is what the Seade line shows.
    // An address with no object behind it yet (a raw ADS pick) has nothing to
    // show and says where the equipment goes once the work order is saved.
    async loadDevice (objectId) {
      this.devices = []
      if (!objectId) {
        return
      }
      try {
        const { devices } = await api.getObjectDevices(objectId)
        this.devices = devices || []
      } catch (err) {
        // A work order must open even if this one lookup fails; the line then
        // simply shows nothing rather than the dialog refusing to appear.
        this.devices = []
      }
    },
    async getLatestVisits () {
      const object = this.form.object
      this.latestEvents = []
      this.addressEvents = []

      if (!object || !object.id || this.mode === 'view') {
        return
      }

      // Newest first, the ones still to come included.
      const { events } = await api.getEvents({
        objectId: object.id,
        limit: 8
      })
      if (!this.form.object || this.form.object.id !== object.id) {
        return
      }

      this.addressEvents = events
      this.latestEvents = events
        .filter(e => e.id !== this.id)
        .slice(0, 5)
    },
    // What the planner has for a technician on the order's day: Kontoris,
    // Valves, Puhkusel, … with the planner's own colour. Empty when nothing.
    plannerFor (userId) {
      if (!userId) return []
      const seen = new Set()
      return (this.schedule.all || [])
        .filter(e => e.user && e.user.id === userId && e.workday && e.workday.name)
        .map(e => ({ name: e.workday.name, color: '#' + String(e.workday.color || '9AAAA4').replace(/^#/, '') }))
        .filter(d => !seen.has(d.name) && seen.add(d.name))
    },
    objectCategory (obj) {
      return obj.type === 1 ? this.calendarCategories.CONTRACT : this.calendarCategories.MAINTENENCE
    },
    // The type the dialog chooses by itself. `fromObject` puts the "objekti
    // järgi" mark on it.
    setDefaultCategory (category, fromObject) {
      this.form.category = category
      this.categoryDefault = category
      this.auto.category = fromObject ? category : null
      this.categoryPayment(category)
    },
    // A technician planned Valves or Kontorivalve on the order's day writes a
    // new töökäsk of his own as Valve, whatever the object. An order for someone
    // else, or one the office writes, follows the object as before.
    isOwnValveDay () {
      const me = this.user && this.user.userId
      return !this.id && this.$hasRole(['tehnic']) && !!me && this.form.technic === me &&
        (this.schedule.all || []).some(event => event.user && event.user.id === me &&
          event.workday && VALVE_WORKDAYS.includes(event.workday.id))
    },
    // The day or the Tehnik changed, or the planner answered. A type chosen by
    // hand stays.
    updateValveDay () {
      const valve = this.isOwnValveDay()
      if (valve === this.valveDay) return
      this.valveDay = valve
      if (this.id || this.form.category !== this.categoryDefault) return
      const obj = this.form.object
      if (valve) {
        this.setDefaultCategory(this.calendarCategories.CALL, false)
      } else if (obj) {
        this.setDefaultCategory(this.objectCategory(obj), true)
      } else {
        this.setDefaultCategory(this.isNotTechnic ? this.calendarCategories.MAINTENENCE : this.calendarCategories.CALL, false)
      }
    },
    isOnCall (userId) {
      return this.schedule.onCall.some(event => event.user.id === userId)
    },
    async destroy () {
      const { valid } = await this.$refs.destroy.validate()
      if (!valid) {
        return
      }
      this.loading = true
      try {
        await api.deleteEvent(this.id, {
          reason: this.deleteReason
        })
        this.loading = false
        this.confirmDelete = null
        this.init(this.id)
        this.$emit('deleted')
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
        this.loading = false
      }

    },
    edit () {
      this.manualMode = 'edit'
    },
    updateTechnic () {
      if (!this.form.technic) {
        const event = this.schedule.inOffice && this.schedule.inOffice[0]
        const sameDay = event && dayjs(this.form.startDate, dateFormat).isSame(dayjs(event.startDate), 'day')
        this.form.technic = sameDay && event.user.id || undefined
        this.auto.technic = this.form.technic || null
      }
    }
  },
  mounted () {
    if (this.event) {
      this.init(this.event)
    }
    if (this.eventId) {
      this.init(this.eventId)
    }
    api.getTechnics().then(({ users }) => {
      this.allTechnics = users
    })
    // Cached for five minutes in api.js, so opening a work order rarely asks.
    this.$store.dispatch('getFeatures')
  }
}
</script>
<style scoped>
.tk-reasons { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr)); gap: 8px; }
.tk-reason { display: grid; gap: 2px; padding: 8px 12px; border-radius: 8px; background: #FFF4E0; color: #5B3700; box-shadow: inset 0 0 0 1.5px #8F5400; font-size: 14px; line-height: 1.4; white-space: pre-line; }
.tk-reason__cap { font-size: 11px; font-weight: 700; letter-spacing: .07em; }
/* The three switches stack as tightly as the Vue 2 form's: Vuetify 3 keeps a
   compact switch row 40px tall, the Vue 2 one is 24px. */
.event-toggles :deep(.v-input) {
  --v-input-control-height: 24px;
}
/* The address under "Märkused" is cut to one line rather than wrapping the
   header: min-width lets the flex item shrink below its text. */
.notes-title {
  min-width: 0;
  line-height: 1.3;
}

/* ---- Töökäsk uues kujunduses (Süsteem -> Seaded) ---------------------------
   The hooldusplaat look of Töölaud and the portal (styles/frame.css), drawn
   here on its own: the dialog is teleported out of the .frame wrapper, so its
   tokens are set on .tk-plate. Only the presentation changes; the fields are
   the same Vuetify inputs. */
.tk-plate {
  --p-ground: #F4F6F5;
  --p-ink: #16322A;
  --p-ink-2: #3E504A;
  --p-ink-3: #5B6B66;
  --p-line: #C9D2CE;
  --p-line-strong: #9AAAA4;
  --p-green: #2E7D32;
  --p-green-dark: #1B5E20;
  --p-green-bg: #EAF3EA;
  --p-flame: #1F5FD1;
  --p-flame-bg: #EEF3FC;
  --p-flame-ink: #2B3F66;
  --p-amber: #8F5400;
  --p-amber-bg: #FFF4E0;
  --p-amber-ink: #5B3700;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-variant-numeric: tabular-nums;
  color: var(--p-ink);
}
.tk-plate :deep(.v-card) { border-radius: 12px; color: var(--p-ink); font-family: inherit; }
.tk-plate :deep(.v-field), .tk-plate :deep(.v-list-item), .tk-plate :deep(.v-label) { font-family: inherit; }
.tk-wide { font-stretch: 125%; }
.tk-mid { font-stretch: 112%; }
.tk-head {
  display: flex; align-items: center; gap: 4px;
  min-height: 46px; padding: 2px 6px 2px 20px;
  border-bottom: 2px solid var(--p-ink);
}
.tk-head--deleted { border-bottom-color: #C62828; }
.tk-head--deleted .tk-title { color: #C62828; }
/* On a narrow phone the date and time go under the title rather than under
   the Akt button. */
.tk-head__text { display: flex; align-items: baseline; flex-wrap: wrap; column-gap: 12px; row-gap: 0; flex-grow: 1; min-width: 0; }
.tk-below { margin-top: 2px; }
.tk-cap { font-size: 12.5px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: var(--p-ink-3); }
.tk-call { display: inline-flex; align-items: center; justify-content: center; min-width: 36px; min-height: 36px; color: #2e7d32; text-decoration: none; }
.tk-title { margin: 0; font-size: 18px; font-weight: 700; line-height: 1.2; color: var(--p-ink); white-space: nowrap; }
/* Labels above the boxes, as on Töölaud and the portal. */
.tk-lbl { font-size: 12px; font-weight: 600; color: var(--p-ink-2); margin: 0 0 2px; line-height: 14px; white-space: nowrap; }
/* 36 px boxes. */
.tk-plate :deep(.v-card-text .v-input) { --v-field-input-padding-top: 6px; --v-field-input-padding-bottom: 6px; font-size: 14.5px; }
/* Boxes are 36 px; a textarea starts there and grows with its text. */
.tk-plate :deep(.v-card-text .v-input:not(.v-textarea)) { --v-input-control-height: 36px; }
.tk-plate :deep(.v-card-text .v-input:not(.v-textarea) .v-field__input) { min-height: 36px; }
.tk-plate :deep(.v-card-text .v-field__input) { font-size: 14.5px; padding-top: 6px; padding-bottom: 6px; }
/* The address and client boxes' icons sat 8 px down and made them 40 px. */
.tk-plate :deep(.v-card-text .v-field__prepend-inner),
.tk-plate :deep(.v-card-text .v-field__append-inner),
.tk-plate :deep(.v-card-text .v-field__clearable) { padding-top: 0; align-items: center; }
.tk-plate :deep(.v-card-text .v-field__input input), .tk-plate :deep(.v-card-text textarea) { font-size: 14.5px; }
/* On a phone, 16px: iOS Safari zooms the whole page in on any field with
   smaller text, and leaves it zoomed. */
@media (max-width: 640px) {
  .tk-plate :deep(.v-card-text .v-input),
  .tk-plate :deep(.v-card-text .v-field__input),
  .tk-plate :deep(.v-card-text .v-field__input input),
  .tk-plate :deep(.v-card-text textarea) { font-size: 16px; }
}
.tk-plate :deep(.v-field--variant-outlined) { border-radius: 8px; --v-field-border-width: 1.5px; --v-field-border-opacity: 1; }
.tk-plate :deep(.v-field--variant-outlined .v-field__outline) { color: var(--p-line-strong); }
.tk-plate :deep(.v-field--variant-outlined .v-field__outline__start) { border-radius: 8px 0 0 8px; }
.tk-plate :deep(.v-field--variant-outlined .v-field__outline__end) { border-radius: 0 8px 8px 0; }
.tk-plate :deep(.v-field--focused .v-field__outline) { color: var(--p-flame); }
.tk-plate :deep(.v-field--disabled .v-field__outline) { color: var(--p-line); }
/* What the dialog filled in by itself, and the technician's time. */
.tk-chip {
  font-size: 11.5px; font-weight: 600; line-height: 16px; white-space: nowrap;
  border: 1px solid; border-radius: 9px; padding: 0 6px; margin-left: 4px;
}
.tk-chip--auto { color: var(--p-flame-ink); background: var(--p-flame-bg); border-color: var(--p-flame); }
/* The planner's word for the technician's day, after the name. */
.tk-day { display: inline-flex; align-items: center; gap: 5px; margin-left: 10px; font-size: 13px; font-weight: 600; color: var(--p-ink-2); white-space: nowrap; }
.tk-day__dot { width: 9px; height: 9px; border-radius: 5px; display: inline-block; box-shadow: inset 0 0 0 1px rgba(0,0,0,.18); }
.tk-tech { display: flex; align-items: center; min-width: 0; }
.tk-tech__name { overflow: hidden; text-overflow: ellipsis; }
/* Compact: the fields sit close, as on the old form. */
.tk-plate :deep(.v-card-text) { padding: 12px 12px 10px !important; }
.tk-plate :deep(.v-card-text .v-container) { padding: 0 8px; }
/* Every column: a half-width one is v-col-sm-6 with no plain v-col class. */
.tk-plate :deep(.v-card-text [class*="v-col"]) { padding-top: 4px; padding-bottom: 4px; }
/* Rows pull in by their columns' padding; matched, or the first labels slip
   under the header and the last fields under the footer. */
.tk-plate :deep(.v-card-text .v-row) { margin-top: -4px; margin-bottom: -4px; }
/* The right column on the plate ground. */
.tk-side { background: var(--p-ground); border-radius: 10px; padding-top: 6px !important; padding-bottom: 6px !important; }
/* Side by side, the right column is just wide enough for its longest line
   ("Tuleviku hoolduse meeldetuletus", a switch label, "Lepinguline" with its
   mark) and the form takes the rest; on a phone they stack as before. A
   max-content width does not work here: Vuetify's inputs report a natural
   width far wider than what they show. */
@media (min-width: 600px) {
  .tk-main { flex: 1 1 0 !important; max-width: none !important; min-width: 0; }
  .tk-side { flex: 0 0 var(--tk-side-w, 244px) !important; max-width: var(--tk-side-w, 244px) !important; white-space: nowrap; }
}
.tk-side :deep(.v-select) { min-width: 100%; }
/* The three switches, 28 px apart. */
.tk-plate :deep(.v-card-text .event-toggles .v-input) { --v-input-control-height: 28px; }
.tk-hist { display: flex; flex-direction: column; gap: 0; border-top: 1px solid var(--p-line); margin-top: 4px; padding-top: 8px !important; }
.tk-hist__title { font-size: 11px; font-weight: 700; letter-spacing: .06em; color: var(--p-ink-3); margin-bottom: 2px; }
.tk-dot { width: 9px; height: 9px; border-radius: 5px; display: inline-block; flex: none; }
.tk-dot--future { background: var(--p-flame); }
.tk-dot--past { background: var(--p-green); }
.tk-hist__row {
  display: grid; grid-template-columns: 9px minmax(0, 1fr); column-gap: 8px; align-items: center;
  width: calc(100% + 12px); margin: 0 -6px; padding: 0 6px; height: 24px;
  border: 0; border-radius: 6px; background: none; text-align: left; cursor: pointer;
  font: inherit; color: inherit;
}
.tk-hist__row:hover { background: #FFFFFF; }
.tk-hist__row:focus-visible { outline: 3px solid var(--p-flame); outline-offset: 1px; }
.tk-hist__line { font-size: 12.5px; color: var(--p-ink-2); white-space: nowrap; }
.tk-hist__line b { color: var(--p-ink); font-weight: 700; margin-right: 8px; }
.tk-foot {
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
  border-top: 1px solid var(--p-line); padding: 8px 14px 8px 20px;
}
.tk-foot__hint { flex-grow: 1; min-width: 0; font-size: 12px; color: var(--p-ink-3); }
.tk-btn { height: 36px !important; min-height: 36px; border-radius: 6px; text-transform: none; letter-spacing: 0; font-weight: 700; font-size: 14.5px; }
</style>
